import { useEffect, useRef, useState, type CSSProperties, type JSX } from 'react';
import { apiPost } from '../api';
import {
  NZPVS_CLICK_AGAIN_LABEL,
  NZPVS_COPIED,
  NZPVS_COPIED_EARLIER,
  NZPVS_COPY_FAILED,
  NZPVS_COPY_LABEL,
  NZPVS_READY,
  PENDING_COPY_TTL_MS,
  copyErrorSentence,
  holdPendingCopy,
  nzpvsCopyPath,
  payloadRefusalSentence,
  payloadTextFor,
  takePendingCopy,
  type PendingCopy,
} from './copy';

/** The browser's clipboard writer, or null where there is none (an insecure origin). */
function clipboardWriter(): ((text: string) => Promise<void>) | null {
  const clipboard = typeof navigator === 'undefined' ? undefined : navigator.clipboard;
  return clipboard && typeof clipboard.writeText === 'function'
    ? (text) => clipboard.writeText(text)
    : null;
}

/** What the status line says: nothing, the copy, "click again", or a refusal. */
type CopyStatus =
  { kind: 'none' } | { kind: 'copied' } | { kind: 'ready' } | { kind: 'error'; text: string };

/** An empty live region that takes no room and no flex line (it stays mounted to announce). */
const UNSEEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * "Copy for NZPVS" on one LODGEABLE lodgement row (cca-uzt5): fetch the row's
 * NZPVS copy (the worker audits it first) and put it on the clipboard for the
 * registrar's CCAANZ → NZPVS bookmark on the Police page. A discrete
 * link-style control, never a second primary button beside "Mark lodged" (the
 * discrete-interface rule).
 *
 * A clipboard write needs the click's own user activation, which a slow fetch
 * can outlive. When the write is refused, the copy is held in a REF — never
 * React state, never storage — the control reads "Click again to copy" (and
 * its status line says so too), and the next click writes it synchronously,
 * inside its own activation. The held copy is dropped after that write, after
 * PENDING_COPY_TTL_MS, or when the row unmounts, whichever comes first.
 *
 * ONE row holds the clipboard at a time (Miranda M-S1): the lodgement section
 * owns which (`onClipboard`), and a row whose copy has since been replaced by
 * another row's says "Copied earlier — no longer on the clipboard." The owner
 * moves when a write SUCCEEDS — that is when the clipboard changes; a copy
 * that is refused leaves the last one where it was.
 *
 * Its status line (role="status") says what happened in words — a refusal in
 * the page's error ink, opening "Not copied —"; everything else muted — and
 * never shows a value from the application. `layout="inline"` is the compact
 * (paged) row's: the button and the status are two flex items of the row, the
 * status a full-width line under it (Miranda M-S3), so the row stays one line
 * until there is something to say.
 */
export function NzpvsCopyButton({
  recordId,
  describedBy,
  layout = 'stacked',
  onClipboard = true,
  onCopied,
}: {
  recordId: string;
  /** Explicitly `| undefined` (exactOptionalPropertyTypes): a row with no notes passes undefined. */
  describedBy?: string | undefined;
  /** `stacked`: button over status (the card). `inline`: two flex items of a compact row. */
  layout?: 'stacked' | 'inline';
  /** Is this row's copy the one on the clipboard now? Absent → treated as yes. */
  onClipboard?: boolean;
  /** Called once this row's copy has been WRITTEN to the clipboard. */
  onCopied?: ((recordId: string) => void) | undefined;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [clickAgain, setClickAgain] = useState(false);
  const [status, setStatus] = useState<CopyStatus>({ kind: 'none' });
  const held = useRef<PendingCopy | null>(null);
  const expiry = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** True while the copy is fetched — read synchronously, so a second press before the
   *  re-render is ignored too. */
  const inFlight = useRef(false);

  /** Forget any held copy and its timer. Refs only, so it is safe at unmount. */
  const drop = () => {
    held.current = null;
    if (expiry.current !== null) {
      clearTimeout(expiry.current);
      expiry.current = null;
    }
  };

  useEffect(() => drop, []);

  const copied = () => {
    setStatus({ kind: 'copied' });
    onCopied?.(recordId);
  };
  const refused = (text: string) => setStatus({ kind: 'error', text });

  async function onClick() {
    if (inFlight.current) return;
    const write = clipboardWriter();
    const heldText = takePendingCopy(held.current, Date.now());
    drop();
    setClickAgain(false);

    if (heldText !== null) {
      // The second click: write NOW, before any await, inside this activation.
      const writing = write ? write(heldText) : Promise.reject(new Error('no clipboard'));
      try {
        await writing;
        copied();
      } catch {
        refused(NZPVS_COPY_FAILED);
      }
      return;
    }

    setStatus({ kind: 'none' });
    inFlight.current = true;
    setBusy(true);
    let text: string | null = null;
    try {
      const served = await apiPost<unknown>(nzpvsCopyPath(recordId), {});
      text = payloadTextFor(served, recordId);
      // Another version: this page is out of date (Miranda r1 S-7) — never "Try again".
      if (text === null) refused(payloadRefusalSentence(served));
    } catch (err) {
      refused(copyErrorSentence(err));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
    if (text === null) return;
    if (!write) {
      refused(NZPVS_COPY_FAILED);
      return;
    }
    try {
      await write(text);
      copied();
    } catch {
      // The activation ran out during the fetch: hold the copy for one more click.
      held.current = holdPendingCopy(text, Date.now());
      expiry.current = setTimeout(() => {
        drop();
        setClickAgain(false);
        setStatus({ kind: 'none' });
      }, PENDING_COPY_TTL_MS);
      setClickAgain(true);
      setStatus({ kind: 'ready' });
    }
  }

  const text =
    status.kind === 'copied'
      ? onClipboard
        ? NZPVS_COPIED
        : NZPVS_COPIED_EARLIER
      : status.kind === 'ready'
        ? NZPVS_READY
        : status.kind === 'error'
          ? status.text
          : '';
  const ink = status.kind === 'error' ? 'var(--color-danger)' : 'var(--color-muted)';

  // NOT the native `disabled` while busy: disabling the focused button drops focus to <body>
  // (measured in Chrome after Enter), stranding a keyboard user before "Click again to copy".
  // aria-disabled says it, and onClick ignores a press while `inFlight`.
  const button = (
    <button
      type="button"
      className="btn btn-link btn-sm"
      aria-disabled={busy ? true : undefined}
      onClick={() => void onClick()}
      aria-describedby={describedBy}
    >
      {busy ? 'Copying…' : clickAgain ? NZPVS_CLICK_AGAIN_LABEL : NZPVS_COPY_LABEL}
    </button>
  );

  if (layout === 'inline') {
    return (
      <>
        {button}
        {/* Always mounted, so its sentence is announced when it arrives (SC 4.1.3).
            Empty, it is out of the flex flow; with a sentence it is the row's
            last, full-width line — `order: 1` puts it under the notes. The
            hidden caret indents it to the name's text, as the notes do. */}
        <div
          role="status"
          data-nzpvs-copy-status=""
          style={
            text
              ? {
                  flexBasis: '100%',
                  order: 1,
                  display: 'flex',
                  gap: 8,
                  padding: '0 4px 10px',
                  fontSize: 12,
                  color: ink,
                }
              : UNSEEN
          }
        >
          {text && (
            <>
              <span aria-hidden="true" className="idr-caret" style={{ visibility: 'hidden' }}>
                ▸
              </span>
              <span style={{ minWidth: 0 }}>{text}</span>
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 2,
        maxWidth: '100%',
      }}
    >
      {button}
      {/* Always mounted, so its sentence is announced when it arrives (SC 4.1.3). */}
      <span
        role="status"
        data-nzpvs-copy-status=""
        style={{ fontSize: 12, color: ink, maxWidth: '24em' }}
      >
        {text}
      </span>
    </span>
  );
}
